/**
 * Configurable session windows for the Sessions chart overlay.
 * Times are America/New_York (ET) wall-clock "HH:mm".
 */

export const MARKET_TZ = 'America/New_York';

export const SESSION_COLOR_PRESETS = [
  'rgba(96, 165, 250, 0.14)',
  'rgba(167, 139, 250, 0.12)',
  'rgba(52, 211, 153, 0.12)',
  'rgba(251, 191, 36, 0.12)',
  'rgba(244, 114, 182, 0.12)',
  'rgba(148, 163, 184, 0.14)',
];

/** Default windows — AM Session 9:30–12:00 ET is on; others available to enable. */
export const DEFAULT_SESSIONS = [
  {
    id: 'am',
    label: 'AM Session',
    start: '09:30',
    end: '12:00',
    color: SESSION_COLOR_PRESETS[0],
    enabled: true,
  },
  {
    id: 'pm',
    label: 'PM Session',
    start: '12:00',
    end: '16:00',
    color: SESSION_COLOR_PRESETS[1],
    enabled: false,
  },
  {
    id: 'premarket',
    label: 'Premarket',
    start: '04:00',
    end: '09:30',
    color: SESSION_COLOR_PRESETS[2],
    enabled: false,
  },
  {
    id: 'afterhours',
    label: 'After Hours',
    start: '16:00',
    end: '20:00',
    color: SESSION_COLOR_PRESETS[4],
    enabled: false,
  },
];

export function cloneDefaultSessions() {
  return DEFAULT_SESSIONS.map((s) => ({ ...s }));
}

/** Parse "HH:mm" or "H:mm" → minutes from midnight. */
export function parseHhMmToMinutes(value) {
  if (value == null) return null;
  const str = String(value).trim();
  const m = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h < 0 || h > 23 || min < 0 || min > 59) {
    return null;
  }
  return h * 60 + min;
}

export function formatMinutesToHhMm(totalMin) {
  const h = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Normalize sessions from params (supports legacy showPremarket/showRth/showAfterhours).
 */
export function normalizeSessions(params = {}) {
  if (Array.isArray(params.sessions) && params.sessions.length > 0) {
    return params.sessions.map((s, idx) => ({
      id: s.id || `session_${idx}`,
      label: s.label || `Session ${idx + 1}`,
      start: s.start || '09:30',
      end: s.end || '12:00',
      color: s.color || SESSION_COLOR_PRESETS[idx % SESSION_COLOR_PRESETS.length],
      enabled: s.enabled !== false,
    }));
  }

  // Legacy boolean flags — migrate to the new AM-session defaults.
  // Old defaults (premarket + AH on) painted near-continuous bands when zoomed out.
  if (
    params.showPremarket != null ||
    params.showRth != null ||
    params.showAfterhours != null
  ) {
    return cloneDefaultSessions();
  }

  return cloneDefaultSessions();
}

/** First matching enabled session id for an ET minute-of-day, or null. */
export function matchSessionId(minutesOfDay, sessions) {
  if (!Number.isFinite(minutesOfDay)) return null;
  for (const s of sessions) {
    if (!s.enabled) continue;
    const start = parseHhMmToMinutes(s.start);
    const end = parseHhMmToMinutes(s.end);
    if (start == null || end == null || start === end) continue;
    if (start < end) {
      if (minutesOfDay >= start && minutesOfDay < end) return s.id;
    } else {
      // Overnight wrap (e.g. 20:00–04:00)
      if (minutesOfDay >= start || minutesOfDay < end) return s.id;
    }
  }
  return null;
}

export function sessionsChipSummary(params = {}) {
  const sessions = normalizeSessions(params).filter((s) => s.enabled);
  if (sessions.length === 0) return 'off';
  if (sessions.length === 1) {
    return `${sessions[0].start}–${sessions[0].end} ET`;
  }
  return `${sessions.length} windows`;
}

export function createBlankSession(existing = []) {
  const idx = existing.length;
  return {
    id: `custom_${Date.now()}_${idx}`,
    label: `Session ${idx + 1}`,
    start: '09:30',
    end: '12:00',
    color: SESSION_COLOR_PRESETS[idx % SESSION_COLOR_PRESETS.length],
    enabled: true,
  };
}
